import React, { useState } from 'react';
import DisModal from './design-system/DisModal';
import DisButton from './design-system/DisButton';
import DisCard from './design-system/DisCard';
import { SUBSCRIPTION_PLANS } from '../services/MonetizationService';

const MonetizationPopup = ({ isOpen, onClose, trialDaysRemaining, onUpgrade }) => {
  const [isYearly, setIsYearly] = useState(false);

  // Filtrer les plans Essential et Family selon la période
  const essentialPlan = SUBSCRIPTION_PLANS.find(plan => 
    (plan.name === 'Essential' || plan.name === 'Essential Annuel') && 
    (isYearly ? plan.duration === 'yearly' : plan.duration === 'monthly')
  );
  const familyPlan = SUBSCRIPTION_PLANS.find(plan => 
    (plan.name === 'Famille' || plan.name === 'Famille Annuel') && 
    (isYearly ? plan.duration === 'yearly' : plan.duration === 'monthly')
  );

  const handleSubscribe = async (plan) => {
    try {
      // Appeler le service d'abonnement avec le plan sélectionné
      if (onUpgrade) {
        onUpgrade(plan);
      }
    } catch (error) {
      console.error('Error subscribing to plan:', error);
      alert('Erreur lors de la souscription au plan');
    }
  };

  return (
    <DisModal
      isOpen={isOpen}
      onClose={onClose}
      title="🎁 Fin de l'essai gratuit"
      size="lg"
    >
      <div className="text-center space-y-6">
        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
          <span className="text-3xl">🎁</span>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {trialDaysRemaining > 0 
              ? `⏰ Plus que ${trialDaysRemaining} jour${trialDaysRemaining > 1 ? 's' : ''} !`
              : '🔒 Votre mois d\'essai est terminé !'
            }
          </h3>
          <p className="text-gray-600 text-lg">
            {trialDaysRemaining > 0
              ? 'Profite encore de quelques jours pour découvrir toute la magie ! ✨'
              : 'Continue à offrir à tes enfants des réponses magiques ! 🌟'
            }
          </p>
        </div>

        {/* Version gratuite */}
        <DisCard className="bg-red-50 border border-red-200 text-left">
          <h4 className="font-bold text-red-700 mb-3 text-center">
            📱 Version gratuite :
          </h4>
          <ul className="space-y-2 text-sm text-red-600">
            <li className="flex items-start">
              <span className="text-red-400 mr-2">•</span>
              Un seul « pourquoi » par jour
            </li>
            <li className="flex items-start">
              <span className="text-red-400 mr-2">•</span>
              Un seul enfant à la fois
            </li>
            <li className="flex items-start">
              <span className="text-red-400 mr-2">•</span>
              Les souvenirs ne sont gardés que 7 jours
            </li>
          </ul>
        </DisCard>

        {/* Toggle Mensuel/Annuel */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4">
          <div className="flex items-center justify-center space-x-4">
            <span className={`font-medium ${!isYearly ? 'text-teal-700' : 'text-gray-500'}`}>
              Mensuel
            </span>
            <div className="relative">
              <input
                type="checkbox"
                checked={isYearly}
                onChange={(e) => setIsYearly(e.target.checked)}
                className="sr-only"
              />
              <div 
                onClick={() => setIsYearly(!isYearly)}
                className={`w-16 h-8 rounded-full cursor-pointer transition-all duration-300 ${
                  isYearly ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div className={`w-6 h-6 bg-white rounded-full shadow-md transform transition-transform duration-300 mt-1 ${
                  isYearly ? 'translate-x-9' : 'translate-x-1'
                }`} />
              </div>
            </div>
            <div className="flex flex-col items-start">
              <span className={`font-medium ${isYearly ? 'text-green-700' : 'text-gray-500'}`}>
                Annuel
              </span>
              {isYearly && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold">
                  2 MOIS OFFERTS
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Plans d'abonnement */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Plan Essential */}
          {essentialPlan && (
            <DisCard className="bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-300 relative">
              <div className="text-center">
                <h4 className="font-bold text-blue-700 mb-3 text-lg">
                  📚 Essential
                </h4>
                
                <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl p-4 mb-3">
                  <div className="text-3xl font-bold">{essentialPlan.price}€</div>
                  <div className="text-sm opacity-90">
                    par {isYearly ? 'an' : 'mois'}
                  </div>
                  {isYearly && essentialPlan.originalPrice && (
                    <div className="text-xs opacity-75 line-through">
                      {essentialPlan.originalPrice}€
                    </div>
                  )}
                </div>

                <ul className="space-y-2 text-sm text-blue-700 mb-4">
                  <li className="flex items-start">
                    <span className="mr-2">✨</span>
                    Ne manquez jamais un « pourquoi »
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">👶</span>
                    1 profil enfant personnalisé
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📚</span>
                    Tous ses souvenirs gardés pour toujours
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🎯</span>
                    Des réponses qui grandissent avec lui
                  </li>
                </ul>

                <DisButton
                  onClick={() => handleSubscribe(essentialPlan)}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  🚀 Choisir Essential
                </DisButton>
              </div>
            </DisCard>
          )}

          {/* Plan Family */}
          {familyPlan && (
            <DisCard className="bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-300 relative">
              <div className="absolute -top-2 -right-2 bg-yellow-500 text-white text-xs px-3 py-1 rounded-full font-bold">
                POPULAIRE
              </div>
              <div className="text-center">
                <h4 className="font-bold text-green-700 mb-3 text-lg">
                  👨‍👩‍👧‍👦 Famille
                </h4>
                
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl p-4 mb-3">
                  <div className="text-3xl font-bold">{familyPlan.price}€</div>
                  <div className="text-sm opacity-90">
                    par {isYearly ? 'an' : 'mois'}
                  </div>
                  {isYearly && familyPlan.originalPrice && (
                    <div className="text-xs opacity-75 line-through">
                      {familyPlan.originalPrice}€
                    </div>
                  )}
                </div>

                <ul className="space-y-2 text-sm text-green-700 mb-4">
                  <li className="flex items-start">
                    <span className="mr-2">✨</span>
                    Tous les « pourquoi », sans limite
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">👨‍👩‍👧‍👦</span>
                    Chaque enfant a son univers
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">📚</span>
                    Tous leurs souvenirs gardés pour toujours
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2">🎯</span>
                    Des réponses qui grandissent avec eux
                  </li>
                </ul>

                <DisButton
                  onClick={() => handleSubscribe(familyPlan)}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  🚀 Choisir Famille
                </DisButton>
              </div>
            </DisCard>
          )}
        </div>

        {trialDaysRemaining > 0 && (
          <DisButton
            variant="ghost"
            onClick={onClose}
            className="w-full"
          >
            ⏰ Continuer l'essai gratuit
          </DisButton>
        )}

        <p className="text-xs text-gray-500">
          💝 Sans engagement • Annulation possible à tout moment
        </p>
      </div>
    </DisModal>
  );
};

export default MonetizationPopup;